import { DRAWER_MODE, PRICING_MODEL } from '@monorepo/shared/schemas/common'
import type { PlanAgentAction, TierInput } from '@monorepo/shared/schemas/plans'
import { Bot, DollarSign, Edit, Loader2, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { ConfirmationDialog } from '@/shared/components/ui/confirmation-dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import { toast } from '@/shared/lib/toast'
import { usePlan, useRemoveAgentAction } from '../../hooks/usePlans'
import { AgentActionDrawer } from './AgentActionDrawer'

interface AgentsUsageTabProps {
  planId: string
}

type PlanAgentActionWithTiers = PlanAgentAction & {
  pricing_tiers?: TierInput[]
}

export function AgentsUsageTab({ planId }: AgentsUsageTabProps) {
  const { data: plan, isLoading } = usePlan(planId)
  const removeAgentAction = useRemoveAgentAction()

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingAction, setEditingAction] =
    useState<PlanAgentActionWithTiers | null>(null)
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>(
    DRAWER_MODE.create
  )

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [actionToDelete, setActionToDelete] =
    useState<PlanAgentActionWithTiers | null>(null)

  const handleAddClick = () => {
    setEditingAction(null)
    setDrawerMode(DRAWER_MODE.create)
    setDrawerOpen(true)
  }

  const handleEditClick = (action: PlanAgentActionWithTiers) => {
    setEditingAction(action)
    setDrawerMode(DRAWER_MODE.edit)
    setDrawerOpen(true)
  }

  const handleDeleteClick = (action: PlanAgentActionWithTiers) => {
    setActionToDelete(action)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!actionToDelete) return

    try {
      await removeAgentAction.mutateAsync({
        planId,
        agentActionId: actionToDelete.id,
      })
      toast.success('Agent action removed successfully')
      setDeleteDialogOpen(false)
      setActionToDelete(null)
    } catch {
      // Error toast is handled by the mutation hook
    }
  }

  const agentActions = plan?.agent_actions || []

  // Helper function to format pricing model display
  const getPricingDisplay = (action: PlanAgentActionWithTiers) => {
    const model = action.pricing_model
    if (!model) return '-'

    if (model === PRICING_MODEL.flat && action.unit_price) {
      return `$${action.unit_price}/unit`
    } else if (
      model === PRICING_MODEL.package &&
      action.package_price &&
      action.package_size
    ) {
      return `$${action.package_price}/${action.package_size} units`
    } else if (
      model === PRICING_MODEL.tiered ||
      model === PRICING_MODEL.volume
    ) {
      const tierCount = action.pricing_tiers?.length || 0
      return `${tierCount} tier${tierCount !== 1 ? 's' : ''}`
    }
    return model
  }

  return (
    <>
      <Card className='bg-transparent border-none shadow-none'>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <div>
              <CardTitle className='flex items-center gap-2'>
                <Bot className='w-5 h-5' />
                Agent Actions (Usage-based)
              </CardTitle>
              <p className='text-sm text-muted-foreground mt-1'>
                Configure usage-based pricing for specific agent actions
              </p>
            </div>
            <Button type='button' size='sm' onClick={handleAddClick}>
              <Plus className='w-4 h-4 mr-2' />
              Add Agent
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='flex items-center justify-center py-12'>
              <Loader2 className='w-6 h-6 animate-spin mr-2 text-muted-foreground' />
              <span className='text-muted-foreground'>
                Loading agent actions...
              </span>
            </div>
          ) : agentActions.length === 0 ? (
            <div className='text-center py-12 border border-dashed rounded-lg bg-muted/20'>
              <Bot className='w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50' />
              <p className='text-muted-foreground mb-4'>
                No agent actions configured yet
              </p>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={handleAddClick}
              >
                <Plus className='w-4 h-4 mr-2' />
                Add Your First Agent
              </Button>
            </div>
          ) : (
            <div className='border rounded-lg overflow-hidden'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent Name</TableHead>
                    <TableHead>Action Name</TableHead>
                    <TableHead>Billable Signal</TableHead>
                    <TableHead>Pricing Model</TableHead>
                    <TableHead>Pricing</TableHead>
                    <TableHead>Included</TableHead>
                    <TableHead>Min Commit</TableHead>
                    <TableHead className='text-right'>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agentActions.map(action => (
                    <TableRow key={action.id}>
                      <TableCell className='font-medium'>
                        <div className='text-sm'>
                          {action.agent?.name || 'Unknown Agent'}
                        </div>
                      </TableCell>
                      <TableCell className='font-medium'>
                        <div className='flex flex-col'>
                          <span className='text-sm'>
                            {action.action?.action_name || 'Unknown Action'}
                          </span>
                          <span className='text-xs text-muted-foreground'>
                            Type: {action.action?.action_type || 'N/A'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {action.billable_signal ? (
                          <div className='flex flex-col'>
                            <span className='text-sm font-medium'>
                              {action.billable_signal.signal_name}
                            </span>
                            <span className='text-xs text-muted-foreground'>
                              {action.billable_signal.signal_code} •{' '}
                              {action.billable_signal.unit_label}
                            </span>
                          </div>
                        ) : (
                          <span className='text-sm text-muted-foreground'>
                            No signal
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant='outline' className='capitalize'>
                          {action.pricing_model || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className='flex items-center gap-1 text-sm'>
                          <DollarSign className='w-3 h-3 text-muted-foreground' />
                          {getPricingDisplay(action)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className='text-sm text-muted-foreground'>
                          {action.included_units || 0} units
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className='text-sm text-muted-foreground'>
                          {action.minimum_commitment || 0} units
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className='flex items-center justify-end gap-2'>
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            onClick={() => handleEditClick(action)}
                          >
                            <Edit className='w-4 h-4' />
                          </Button>
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            onClick={() => handleDeleteClick(action)}
                            disabled={removeAgentAction.isPending}
                          >
                            <Trash2 className='w-4 h-4 text-destructive' />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Agent Action Drawer */}
      <AgentActionDrawer
        planId={planId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        editingAction={editingAction ?? undefined}
        mode={drawerMode}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title='Delete Agent Action'
        description='Are you sure you want to remove this agent action from the plan? This action cannot be undone.'
        confirmText='Delete'
        cancelText='Cancel'
        variant='destructive'
        onConfirm={handleDeleteConfirm}
        onCancel={() => setActionToDelete(null)}
      />
    </>
  )
}
